'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  Calendar,
  DollarSign,
  XCircle,
  Clock,
  Home,
  Shield,
  Gavel,
  ChevronRight,
  Info
} from 'lucide-react';

const sections = [
  { id: 'introduction', title: '1. Introduction & Agreement', icon: FileText },
  { id: 'booking', title: '2. Booking & Reservations', icon: Calendar },
  { id: 'payment', title: '3. Payments & Deposits', icon: DollarSign },
  { id: 'cancellation', title: '4. Cancellation & Refund', icon: XCircle },
  { id: 'checkin-checkout', title: '5. Check-in & Check-out', icon: Clock },
  { id: 'house-rules', title: '6. Guest Rules & Conduct', icon: Home },
  { id: 'damage-liability', title: '7. Damage & Liability', icon: Shield },
  { id: 'governing-law', title: '8. Governing Law', icon: Gavel },
];

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

export default function TermsPageClient() {
  const [activeSection, setActiveSection] = useState('introduction');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0.1 }
    );

    sections.forEach((section) => {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const headerOffset = 100;
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(id);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white text-gray-800 font-dm-sans">
      {/* Hero Header Section */}
      <section className="relative py-24 overflow-hidden h-[40vh] min-h-[300px] flex items-center justify-center">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-black/60 z-10"></div>
          <img
            src="/images/downtown-dubai.jpg"
            alt="Dubai Skyline"
            className="w-full h-full object-cover object-center"
          />
        </div>

        {/* Content */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-space-grotesk font-bold text-white mb-4">
              Terms & Conditions
            </h1>
            <p className="text-lg text-gray-200 max-w-2xl mx-auto font-light">
              Please read these Terms of Service carefully before booking or renting our luxury holiday home properties in Dubai.
            </p>
            <div className="mt-4 text-sm text-[#FFC400] font-medium">
              Last Updated: May 2026
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content Layout */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          
          {/* Sidebar Table of Contents (Sticky) */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-28 bg-white rounded-xl shadow-md border border-gray-100 p-6 space-y-2">
              <h3 className="font-space-grotesk font-bold text-lg mb-4 text-gray-900 border-b pb-2">
                Table of Contents
              </h3>
              <nav className="flex flex-col space-y-1">
                {sections.map((section) => {
                  const IconComponent = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? 'bg-[#FFC400]/10 text-gray-900 border-l-4 border-[#FFC400] pl-2 font-semibold'
                          : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 border-l-4 border-transparent'
                      }`}
                    >
                      <IconComponent className={`w-4 h-4 shrink-0 ${isActive ? 'text-[#FFC400]' : 'text-gray-400'}`} />
                      <span className="truncate">{section.title}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Detailed Document Content */}
          <div className="col-span-1 lg:col-span-3 space-y-12">
            
            {/* Disclaimer Alert */}
            <motion.div
              variants={fadeInUp}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex gap-4 text-amber-900"
            >
              <Info className="w-6 h-6 text-[#FFC400] shrink-0 mt-0.5" />
              <div>
                <h4 className="font-bold font-space-grotesk text-base mb-1">Important Information</h4>
                <p className="text-sm text-amber-800 leading-relaxed">
                  By booking a property with 18Bricks (referred to as &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), you (referred to as the &quot;Guest,&quot; &quot;Tenant,&quot; or &quot;Client&quot;) agree to be legally bound by these Terms & Conditions. All holiday home rentals are registered with and regulated by the Dubai Department of Economy and Tourism (DET / DTCM).
                </p>
              </div>
            </motion.div>

            {/* Sections */}
            <div className="space-y-12">
              
              {/* Section 1: Introduction */}
              <motion.div
                id="introduction"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28 bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-4">
                  <div className="w-10 h-10 bg-[#FFC400]/10 rounded-lg flex items-center justify-center text-[#FFC400]">
                    <FileText className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-space-grotesk font-bold text-gray-900">1. Introduction & Agreement</h2>
                </div>
                <p className="leading-relaxed">
                  Welcome to 18Bricks. These Terms & Conditions govern your relationship with us and outline your rights and obligations when booking, checking in, and occupying any property listed on our platform or managed by us.
                </p>
                <p className="leading-relaxed">
                  By completing a booking on our website, through third-party travel agents, or directly with our staff, you confirm that you are at least 21 years of age, possess the legal authority to enter into agreement, and accept all details contained herein without reservation.
                </p>
              </motion.div>

              {/* Section 2: Booking */}
              <motion.div
                id="booking"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28 bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-4">
                  <div className="w-10 h-10 bg-[#FFC400]/10 rounded-lg flex items-center justify-center text-[#FFC400]">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-space-grotesk font-bold text-gray-900">2. Booking & Reservations</h2>
                </div>
                <p className="leading-relaxed">
                  Reservations are considered confirmed only upon receipt of the full or partial payment (as specified in your booking confirmation) and our formal written booking confirmation email.
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm leading-relaxed text-gray-600">
                  <li><strong>Occupancy Limits:</strong> The number of guests allowed to sleep in the property is strictly limited to the number declared at booking. Extra guests are subject to approval and additional charges.</li>
                  <li><strong>Passport Submission:</strong> In compliance with the Dubai Department of Economy and Tourism (DET), all guests (including children) must upload clear copies of their passport or UAE National ID at least 48 hours prior to check-in for registration.</li>
                  <li><strong>Booking Rights:</strong> We reserve the right to refuse booking requests at our sole discretion without needing to specify reasons.</li>
                </ul>
              </motion.div>

              {/* Section 3: Payments */}
              <motion.div
                id="booking"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28"
              /> {/* Spacer anchor */}

              <motion.div
                id="payment"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28 bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-4">
                  <div className="w-10 h-10 bg-[#FFC400]/10 rounded-lg flex items-center justify-center text-[#FFC400]">
                    <DollarSign className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-space-grotesk font-bold text-gray-900">3. Rates, Payments & Security Deposit</h2>
                </div>
                <p className="leading-relaxed">
                  All property rates are specified in United Arab Emirates Dirhams (AED) unless stated otherwise. Rates are dynamic and subject to change without prior notice.
                </p>
                <div className="grid md:grid-cols-2 gap-6 mt-4">
                  <div className="border border-gray-100 rounded-lg p-4 bg-gray-50">
                    <h5 className="font-bold text-[#FFC400] mb-2 font-space-grotesk">Fees & Tourism Dirham</h5>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      All reservations are subject to:
                      <br />- <strong>Tourism Dirham Fee:</strong> A mandatory tax set by DTCM (typically AED 10 to AED 15 per bedroom per night for up to 30 consecutive nights).
                      <br />- <strong>Standard VAT:</strong> 5% UAE Value Added Tax.
                      <br />- <strong>Service Charges & Cleaning Fees:</strong> As calculated at checkout.
                    </p>
                  </div>
                  <div className="border border-gray-100 rounded-lg p-4 bg-gray-50">
                    <h5 className="font-bold text-[#FFC400] mb-2 font-space-grotesk">Refundable Security Deposit</h5>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      A refundable security deposit is required prior to check-in to cover potential damages, missing items, fine violations, or excessive utility charges. 
                      This deposit will be refunded within 7 to 14 business days post check-out, subject to inspection of the property.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Section 4: Cancellation */}
              <motion.div
                id="cancellation"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28 bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-4">
                  <div className="w-10 h-10 bg-[#FFC400]/10 rounded-lg flex items-center justify-center text-[#FFC400]">
                    <XCircle className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-space-grotesk font-bold text-gray-900">4. Cancellations, Modifications & Refunds</h2>
                </div>
                <p className="leading-relaxed">
                  Our cancellation policy depends on the rate type chosen during the booking process:
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <ChevronRight className="w-4 h-4 text-[#FFC400] shrink-0 mt-1" />
                    <p className="text-sm"><strong>Standard Rate:</strong> Full refund if cancelled 14 days or more before the check-in date. 50% refund if cancelled between 7 and 14 days before check-in. Non-refundable if cancelled less than 7 days prior to arrival.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <ChevronRight className="w-4 h-4 text-[#FFC400] shrink-0 mt-1" />
                    <p className="text-sm"><strong>Non-Refundable Rate:</strong> Under no circumstances will refunds be issued for non-refundable reservations.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <ChevronRight className="w-4 h-4 text-[#FFC400] shrink-0 mt-1" />
                    <p className="text-sm"><strong>Modifications:</strong> Date changes are subject to property availability, rate differences, and approval from 18Bricks management.</p>
                  </div>
                </div>
              </motion.div>

              {/* Section 5: Check-in/Check-out */}
              <motion.div
                id="checkin-checkout"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28 bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-4">
                  <div className="w-10 h-10 bg-[#FFC400]/10 rounded-lg flex items-center justify-center text-[#FFC400]">
                    <Clock className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-space-grotesk font-bold text-gray-900">5. Check-in, Check-out & Occupancy</h2>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <h5 className="font-bold text-gray-900">Check-in: From 3:00 PM</h5>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Early check-in requests are subject to availability and may incur extra fees. A member of our Guest Relations team will meet you at the property to complete registration, verify passports, and hand over keys.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <h5 className="font-bold text-gray-900">Check-out: By 11:00 AM</h5>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Late check-outs must be requested in advance. Unauthorized late check-out is subject to an hourly fine of AED 250, up to the full equivalent of a night&apos;s stay. Keys and access cards must be returned upon departure.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Section 6: House Rules */}
              <motion.div
                id="house-rules"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28 bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-4">
                  <div className="w-10 h-10 bg-[#FFC400]/10 rounded-lg flex items-center justify-center text-[#FFC400]">
                    <Home className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-space-grotesk font-bold text-gray-900">6. House Rules & Guest Conduct</h2>
                </div>
                <p className="leading-relaxed">
                  Guests must behave in a responsible and respectful manner, complying with building rules and UAE local laws:
                </p>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <div className="p-3 border border-red-100 bg-red-50/50 rounded-lg">
                    <span className="font-bold text-red-950 block mb-1">Strictly Prohibited</span>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>No parties, social gatherings, or events.</li>
                      <li>No smoking indoors (subject to AED 2,500 deep-cleaning fine).</li>
                      <li>No illegal activities or commercial filming.</li>
                      <li>No pets allowed (unless explicitly approved).</li>
                    </ul>
                  </div>
                  <div className="p-3 border border-blue-100 bg-blue-50/50 rounded-lg">
                    <span className="font-bold text-blue-950 block mb-1">Guest Requirements</span>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>Keep noise to a minimum (quiet hours 10 PM - 8 AM).</li>
                      <li>Adhere to community pool and gym rules.</li>
                      <li>Proper disposal of garbage in designated areas.</li>
                      <li>Lock all doors and windows when leaving.</li>
                    </ul>
                  </div>
                </div>
              </motion.div>

              {/* Section 7: Property Care */}
              <motion.div
                id="damage-liability"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28 bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-4">
                  <div className="w-10 h-10 bg-[#FFC400]/10 rounded-lg flex items-center justify-center text-[#FFC400]">
                    <Shield className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-space-grotesk font-bold text-gray-900">7. Property Care, Damage & Indemnification</h2>
                </div>
                <p className="leading-relaxed font-semibold text-gray-700">
                  You are responsible for keeping the property and all furniture, fixtures, and fittings in the same state of repair and condition as at the start of the stay.
                </p>
                <p className="leading-relaxed">
                  Any damage or breakages must be reported to 18Bricks immediately. We inspect each home thoroughly upon your departure. You will be billed for the replacement cost of any damaged or missing items, extra cleaning required for extreme dirtiness, or fines issued by building security/community management.
                </p>
                <p className="leading-relaxed text-sm text-gray-500">
                  Disclaimer: 18Bricks, its employees, and property owners accept no liability for any physical injury, illness, loss, or damage to personal property suffered by guests during their stay in our properties.
                </p>
              </motion.div>

              {/* Section 8: Governing Law */}
              <motion.div
                id="governing-law"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28 bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-4">
                  <div className="w-10 h-10 bg-[#FFC400]/10 rounded-lg flex items-center justify-center text-[#FFC400]">
                    <Gavel className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-space-grotesk font-bold text-gray-900">8. Governing Law & Dispute Resolution</h2>
                </div>
                <p className="leading-relaxed">
                  These Terms & Conditions are governed by and construed in accordance with the laws of the Emirate of Dubai and the federal laws of the United Arab Emirates.
                </p>
                <p className="leading-relaxed">
                  Any dispute, controversy, or claim arising out of or in connection with these Terms, including booking contracts, shall be subject to the exclusive jurisdiction of the competent courts of Dubai, United Arab Emirates.
                </p>
              </motion.div>

            </div>
          </div>

        </div>
      </section>
    </div>
  );
}
